/**
 * App-wide top progress bar — the global "something's happening" signal (loading-feedback convention,
 * docs/design/loading-feedback.md). Driven entirely by the in-flight-request counter in api/pending.ts
 * (every `apiFetch` brackets itself), so it reflects ALL network activity with zero per-call code.
 *
 * Behaviour (nprogress-style): a short show-delay so ultra-fast requests don't flash the bar; while
 * requests are in flight it trickles toward ~90%; when the count hits 0 it completes to 100% and fades.
 * It's decorative for assistive tech (aria-hidden) — authoritative status is spoken per-action via the
 * existing aria-live announcements, so the bar never spams a screen reader. Respects reduced-motion
 * (CSS drops the width animation).
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { subscribePending, getPendingCount } from "../api/pending.js";

const SHOW_DELAY_MS = 120; // don't flash the bar for near-instant requests
const FADE_MS = 350; // time to finish + fade after the last request settles

export function TopProgressBar(): JSX.Element | null {
  const pending = useSyncExternalStore(subscribePending, getPendingCount, () => 0);
  const [active, setActive] = useState(false);
  const [width, setWidth] = useState(0);
  const trickle = useRef<ReturnType<typeof setInterval> | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const stopTrickle = () => {
      if (trickle.current !== null) {
        clearInterval(trickle.current);
        trickle.current = null;
      }
    };

    if (pending > 0) {
      if (hideTimer.current !== null) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      if (!active && showTimer.current === null) {
        showTimer.current = setTimeout(() => {
          showTimer.current = null;
          setActive(true);
          setWidth(8);
          stopTrickle();
          // Ease toward 90% — never reaches 100 until the requests actually settle.
          trickle.current = setInterval(() => {
            setWidth((w) => (w < 90 ? w + Math.max(0.5, (90 - w) * 0.08) : w));
          }, 200);
        }, SHOW_DELAY_MS);
      }
    } else {
      if (showTimer.current !== null) {
        clearTimeout(showTimer.current);
        showTimer.current = null;
      }
      stopTrickle();
      if (active && hideTimer.current === null) {
        setWidth(100);
        hideTimer.current = setTimeout(() => {
          hideTimer.current = null;
          setActive(false);
          setWidth(0);
        }, FADE_MS);
      }
    }
  }, [pending, active]);

  // Clear any live timers on unmount.
  useEffect(
    () => () => {
      if (trickle.current !== null) clearInterval(trickle.current);
      if (showTimer.current !== null) clearTimeout(showTimer.current);
      if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    },
    [],
  );

  if (!active) return null;
  return <div className="td-progress" style={{ width: `${width}%` }} aria-hidden="true" />;
}
